import React, {useEffect, useReducer, useState} from 'react'
import {CardDefinitionVariables} from '../../../common/types'
import {Variable} from '../../../common/types/variable-engine'
import {validNameRegex} from '../../../common/variable-engine'
import {ButtonGroup} from '../../../components/form'
import {Button} from '../../../components/library/buttons'
import {TextInput} from '../../../components/library/form'
import styled from 'styled-components'
import {Toggle} from '../../../components/library/toggles/toggle'
import {useFocusFirstFormInput} from '../../../lib/hooks'
import {omit} from '../../../lib/updaters'
import {Updator} from '../editor'
import {validateName, validateVariable} from './helpers'
import {ProfileVariableForm} from './profile-variable-form'
import {ScriptForm} from './script-form'
import {
  BasicVariableTypeSelector,
  BooleanForm,
  DateForm,
  ListForm,
  NumberForm,
  StringForm,
  SwitchForm
} from './variable-form-components'
import {FormState, initVariableState, variableReducer} from './variable-state'
import {EnvironmentNode} from '../../../environment'

const Error = styled.div`
  min-height: 60px;
  display: flex;
  align-items: flex-center;
  padding: 20px;
  border-radius: ${p => p.theme.borderRadius};
  line-height: ${p => p.theme.lineHeight};
  background: ${p => p.theme.redLightest};
  color: ${p => p.theme.red};
`

export interface CommonVariableComponentProps {
  name: string
  variable: Variable
  updateVariables: (
    variables: Updator<CardDefinitionVariables>,
    rename?: {from: string; to: string}
  ) => void
  onSave: (name: string, variable: Variable) => void
  onCancel: () => void
  editing: boolean
  variables?: CardDefinitionVariables
  variablePaths: string[]
}

interface VariableFormProps extends CommonVariableComponentProps {
  environment: EnvironmentNode
  setDirty?: (dirty: boolean) => void
  setOrder: (order: 'up' | 'down' | undefined) => void
  handleClose?: () => void
  readOnly?: boolean
  providerId?: string
}

export type RootMode = 'custom' | 'user-profile'
const variableModeOptions: {
  value: RootMode
  text: string
}[] = [
  {value: 'custom', text: 'Custom'},
  {value: 'user-profile', text: 'User profile'}
]

const isEmpty = (obj: object) => !Object.values(obj).find(e => !!e)

const VariableFormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`

export const CardVariableForm = ({
  environment,
  name,
  variable,
  updateVariables,
  onSave,
  editing,
  variables,
  variablePaths,
  setDirty,
  setOrder,
  handleClose,
  readOnly,
  providerId
}: VariableFormProps) => {
  const [state, dispatch] = useReducer(
    variableReducer,
    {name, variable},
    initVariableState
  )

  const activeVariable:
    | FormState['string']
    | FormState['boolean']
    | FormState['number']
    | FormState['date']
    | FormState['switch']
    | FormState['script']
    | FormState['list'] = state[state.activeVarType]

  const [errors, setErrors] = useState<{[field: string]: string | undefined}>(
    {}
  )

  useEffect(() => {
    if (setDirty) {
      setDirty(state.dirty)
    }
  }, [state.dirty, setDirty])

  const validate = (mode: 'custom' | 'user-profile') => {
    let validationErrors = validateVariable(activeVariable)
    const nameError = validateName(
      {name, newName: state.name},
      variablePaths,
      mode
    )

    if (nameError) {
      validationErrors = {
        ...validationErrors,
        name: nameError
      }
    }
    setErrors(validationErrors)
    return validationErrors
  }

  const save = () => {
    const validationErrors = validate(state.mode)
    const newName =
      state.mode === 'custom'
        ? state.name
        : 'profile.' + state.userProfileSnapFieldName
    if (isEmpty(validationErrors)) {
      const renamed = name && name !== newName
      updateVariables(
        current => ({
          ...(renamed ? omit(current, name) : current),
          [newName]: activeVariable
        }),
        renamed ? {from: name, to: newName} : undefined
      )
      onSave(newName, activeVariable)
      // TODO disabled until we have a way to block
      // changes when card is pending approval
      // see https://www.notion.so/atomic-io/When-card-template-in-Awaiting-approval-state-still-allows-Editor-to-edit-or-add-a-trigger-or-vari-42754e74dd5549e18de135bb14f176ab
      // toaster(editing ? 'Variable updated' : 'Variable created')
    }
  }

  const modalRef = useFocusFirstFormInput<HTMLDivElement>()
  const printableVarName =
    state.mode === 'custom'
      ? state.name
      : state.userProfileSnapFieldName
      ? 'profile.' + state.userProfileSnapFieldName
      : ''

  return (
    <>
      <VariableFormWrapper ref={modalRef}>
        {Object.entries(errors).map(([name, err]) =>
          err ? <Error key={name}>{err}</Error> : null
        )}
        <Toggle<'custom' | 'user-profile'>
          options={variableModeOptions}
          value={state.mode || 'custom'}
          onChange={rootMode =>
            dispatch({type: 'setRootMode', payload: rootMode})
          }
          css={`
            width: 100%;
          `}
          readOnly={readOnly}
          legacyMode={true}
        />

        {state.mode === 'custom' ? (
          <TextInput
            label="Give it a name..."
            upLabel="Name"
            value={state.name}
            onChange={v => {
              dispatch({type: 'setCustomName', payload: v})
              const nameError = validateName(
                {name, newName: v},
                variablePaths,
                state.mode
              )
              if (nameError) {
                setErrors(e => ({...e, name: nameError}))
              } else {
                setErrors(e => ({...e, name: undefined}))
              }
            }}
            pattern={validNameRegex}
            readOnly={readOnly}
          />
        ) : (
          <ProfileVariableForm
            dispatch={dispatch}
            profileFieldName={state.userProfileSnapFieldName}
            readOnly={readOnly}
          />
        )}
        <BasicVariableTypeSelector
          type={
            activeVariable.type === 'switch' ? 'string' : activeVariable.type
          }
          onChange={v => dispatch({type: 'setType', payload: v})}
          // this mode means the type is
          // determined by the user-profile field type
          disabled={state.mode === 'user-profile' || readOnly}
          readOnly={readOnly}
        />
        {activeVariable.type === 'boolean' ? (
          <BooleanForm
            varName={printableVarName}
            activeVariable={activeVariable}
            dispatch={dispatch}
            readOnly={readOnly}
            providerId={providerId}
            mode={state.mode}
          />
        ) : activeVariable.type === 'script' ? (
          <ScriptForm
            environment={environment}
            varName={printableVarName}
            activeVariable={activeVariable}
            dispatch={dispatch}
            readOnly={readOnly}
            providerId={providerId}
          />
        ) : activeVariable.type === 'date' ? (
          <DateForm
            varName={printableVarName}
            mode={state.mode}
            activeVariable={activeVariable}
            dispatch={dispatch}
            readOnly={readOnly}
            providerId={providerId}
          />
        ) : activeVariable.type === 'number' ? (
          <NumberForm
            varName={printableVarName}
            activeVariable={activeVariable}
            dispatch={dispatch}
            readOnly={readOnly}
            providerId={providerId}
            mode={state.mode}
          />
        ) : activeVariable.type === 'string' ? (
          <StringForm
            mode={state.mode}
            activeVariable={activeVariable}
            dispatch={dispatch}
            variablePaths={variablePaths}
            varName={printableVarName}
            readOnly={readOnly}
            providerId={providerId}
          />
        ) : activeVariable.type === 'switch' ? (
          <SwitchForm
            activeVariable={activeVariable}
            dispatch={dispatch}
            state={state}
            name={name}
            setOrder={setOrder}
            variablePaths={variablePaths}
            variables={variables}
            readOnly={readOnly}
          />
        ) : activeVariable.type === 'list' ? (
          <ListForm
            varName={printableVarName}
            activeVariable={activeVariable}
            dispatch={dispatch}
            variablePaths={variablePaths}
            readOnly={readOnly}
            providerId={providerId}
            mode={state.mode}
          />
        ) : null}
      </VariableFormWrapper>
      <ButtonGroup>
        {readOnly ? (
          <Button onClick={handleClose} size="large">
            Close
          </Button>
        ) : (
          <>
            <Button
              type="submit"
              size="large"
              disabled={!printableVarName || readOnly}
              onClick={save}
            >
              {editing ? 'Update' : 'Create'}
            </Button>
            <Button onClick={handleClose} appearance="tint" size="large">
              Cancel
            </Button>
          </>
        )}
      </ButtonGroup>
    </>
  )
}
